import { useState } from 'react';
import {
  faComments,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Body, Button, EmptyState, InlineAlert, Skeleton } from '@e2n/ui/v2';
import { useTranslation } from 'react-i18next';
import { useChatHistory } from '../hooks/useChatHistory';
import type { ChatHistoryState } from '../hooks/useChatHistory';
import type { ChatErrorCode } from '../types/types.ts';
import { ChatHistoryGroup } from './ChatHistoryGroup';

export function ChatHistory() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const history = useChatHistory(query);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <HistorySearch query={query} onChange={setQuery} />
      {history.isStreaming && (
        <Body size="sm" className="text-labels-secondary">
          {t('domain.ai.chat.history.streamingHint')}
        </Body>
      )}
      <HistoryList history={history} isSearching={query.trim() !== ''} />
      <Body
        size="sm"
        className="border-t border-neutrals-v2-300 pt-3 text-labels-secondary">
        {t('domain.ai.chat.history.footer')}
      </Body>
    </div>
  );
}

// TODO: Durch das v2-Input ersetzen, sobald es aus @e2n/ui/v2 exportiert wird
function HistorySearch({
  query,
  onChange,
}: {
  query: string;
  onChange: (query: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex h-9 w-full items-center gap-2 rounded-full border-2 border-neutrals-v2-200 bg-backgrounds-primary px-3 text-sm leading-5 transition-colors focus-within:border-brand-blue-100">
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        aria-hidden
        className="size-4 shrink-0 text-labels-secondary"
      />
      <input
        value={query}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('domain.ai.chat.history.search')}
        aria-label={t('domain.ai.chat.history.search')}
        className="min-w-0 flex-1 bg-transparent text-labels-primary outline-none placeholder:text-labels-secondary"
      />
      {query && (
        <Button
          variant="ghost"
          size="small"
          iconOnly
          aria-label={t('domain.ai.chat.history.clearSearch')}
          onClick={() => onChange('')}
          className="-mr-2">
          <FontAwesomeIcon icon={faXmark} className="size-4" />
        </Button>
      )}
    </div>
  );
}

function HistoryList({
  history,
  isSearching,
}: {
  history: ChatHistoryState;
  isSearching: boolean;
}) {
  const { error, isLoading, hasLoaded, groups } = history;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {error && <HistoryError code={error} />}
      {isLoading && <HistorySkeleton />}
      {hasLoaded && groups.length === 0 && (
        <HistoryEmpty isSearching={isSearching} />
      )}
      {groups.map((group) => (
        <ChatHistoryGroup key={group.key} group={group} history={history} />
      ))}
    </div>
  );
}

function HistoryError({ code }: { code: ChatErrorCode }) {
  const { t } = useTranslation();

  return (
    <InlineAlert
      size="sm"
      className="mb-2"
      description={
        code === 'SESSION_NOT_FOUND'
          ? t('domain.ai.chat.history.notFound')
          : t(`domain.ai.chat.errors.${code}`)
      }
    />
  );
}

function HistoryEmpty({ isSearching }: { isSearching: boolean }) {
  const { t } = useTranslation();

  return (
    <EmptyState
      className="my-auto"
      icon={<FontAwesomeIcon icon={faComments} />}
      title={
        isSearching
          ? t('domain.ai.chat.history.noResults')
          : t('domain.ai.chat.history.empty')
      }
    />
  );
}

function HistorySkeleton() {
  return (
    <div role="status" aria-busy className="flex flex-col gap-4 px-2 pt-2">
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="flex flex-col gap-1.5">
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}
